import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { CoverSurface } from '@/components/CoverSurface';
import { HaiIcon } from '@/components/icons';
import { prepareImageUpload } from '@/features/uploads/svgConvert/prepareImageUpload';
import { IMAGE_PICKER_ACCEPT, isDeclaredSvg } from '@/features/uploads/svgConvert/svgDetect';
import { COVER_ACCEPT_MIME, COVER_MAX_UPLOAD_BYTES } from '@/lib/coverImageFile';
import type { Cover } from '@/types';

import { COVER_SWATCHES } from './coverSwatches';

/**
 * 封面選擇器(R245)— 對齊 D3 `projects.js` `formHTML()` 的封面區
 * -----------------------------------------------------------------------------
 * 版型:可點擊的預覽區(上)+ 色票列 + 上傳鈕(下)+ 一行 hint。
 *
 * ⚠️ **圖片在這裡「只選不傳」**。`POST .../cover` 需要 pid,而建立專案時 pid 還不
 * 存在 → 這個元件只把 `File` 交給父層,由父層在「create 成功之後」再上傳。這是
 * BE 契約決定的兩步流程,不是可以在這層解掉的。
 *
 * **R293 拆成三個出口**(D3 的建立/編輯表單把預覽放在燈箱最上方、色票列留在最後
 * 的「想用什麼當封面？」欄位,中間隔著名稱/說明/品牌三個欄位 → 兩塊必須能分開放):
 *
 *   - {@link useCoverPicker} — 狀態與行為(object URL 生命週期 / MIME 檢查 / 開檔案
 *     對話框);**一個表單只呼叫一次**,兩塊 UI 共用同一份 controller。
 *   - {@link CoverPreview}   — 預覽區(整塊可點 = 上傳)。
 *   - {@link CoverControls}  — 色票列 + 上傳鈕 + hint;**隱藏的 `<input type=file>`
 *     掛在這裡**,所以 `CoverPreview` 一定要跟它同時出現(預覽的點擊要透過同一個
 *     input 才開得起來)。
 *   - {@link CoverPicker}    — 上下相接的組合版(既有呼叫端的 API 一字未動)。
 */

/** 使用者在表單裡的選擇;圖片只帶 `File`(preview URL 由本元件自己管生命週期)。 */
export type CoverDraft =
  | { kind: 'none' }
  | { kind: 'color'; color: string }
  | { kind: 'image'; file: File };

interface UseCoverPickerArgs {
  value: CoverDraft;
  onChange: (next: CoverDraft) => void;
  disabled?: boolean;
}

/** {@link useCoverPicker} 的回傳;`CoverPreview` / `CoverControls` 都吃這一份。 */
export interface CoverPickerController {
  value: CoverDraft;
  disabled: boolean;
  /** 預覽用的 Cover(圖片走本地 object URL,還沒上傳拿不到 BE 的 data URI)。 */
  previewCover: Cover;
  /** 本地 MIME 檢查訊息(null = 沒有錯);顯示在 `CoverControls` 的 hint 位置。 */
  error: string | null;
  openFileDialog: () => void;
  selectColor: (hex: string) => void;
  /** 已選圖片 → 退回第一個色票。 */
  backToColor: () => void;
  inputRef: RefObject<HTMLInputElement>;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function useCoverPicker({
  value,
  onChange,
  disabled = false,
}: UseCoverPickerArgs): CoverPickerController {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // ── object URL 生命週期 ────────────────────────────────────────────────────
  // 刻意由本層持有(而不是塞進 draft):draft 只帶 File,URL 的建立與 revoke 就
  // 綁在同一個 effect 裡,換檔 / 換成色塊 / unmount 三種情況都不會漏 revoke。
  const file = value.kind === 'image' ? value.file : null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const openFileDialog = (): void => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files?.[0];
    // 同一個檔案連選兩次也要能觸發 change → 讀完就清 value
    e.target.value = '';
    if (!picked) return;

    const accept = (file: File) => {
      // MIME 只擋「一定過不了」的:BE 看 magic byte,accept 屬性擋不住拖曳 / 「所有
      // 檔案」選取。⚠️ **大小刻意不在這裡擋** —— 送出前會先縮圖,手機直出的 8 MB
      // 照片縮完只有幾百 KB,在選檔當下就以原始大小拒絕會擋掉本來會成功的檔案。
      if (file.type && !(COVER_ACCEPT_MIME as ReadonlyArray<string>).includes(file.type)) {
        setError(t('cover.errors.COVER_MIME_NOT_ALLOWED'));
        return;
      }
      setError(null);
      onChange({ kind: 'image', file });
    };

    // R415 — SVG 先過轉檔確認(白底);取消 = 封面不動。轉好的 PNG / JPG 照常走上面的檢查。
    // ⚠️ 一般圖片**維持同步**進 draft:封面跟「建立專案」在同一個視窗,改成非同步的話選完
    // 立刻按建立會漏掉封面。所以這裡只認宣告為 SVG 的檔(偽裝成 PNG 的 SVG 由 BE magic
    // byte 擋,跟以前一樣)。
    if (!isDeclaredSvg(picked)) {
      accept(picked);
      return;
    }
    void prepareImageUpload(picked, { allowTransparent: false, maxBytes: COVER_MAX_UPLOAD_BYTES }).then(
      (file) => {
        if (file) accept(file);
      },
    );
  };

  const previewCover: Cover =
    value.kind === 'image' && previewUrl
      ? { kind: 'image', color: null, image: previewUrl }
      : value.kind === 'color'
        ? { kind: 'color', color: value.color, image: null }
        : { kind: 'none', color: null, image: null };

  return {
    value,
    disabled,
    previewCover,
    error,
    openFileDialog,
    selectColor: (hex: string) => {
      setError(null);
      onChange({ kind: 'color', color: hex });
    },
    backToColor: () => onChange({ kind: 'color', color: COVER_SWATCHES[0].hex }),
    inputRef,
    onFileChange,
  };
}

interface CoverPreviewProps {
  picker: CoverPickerController;
  /** 疊在預覽上的文字(專案名稱);空字串顯示 placeholder。 */
  previewLabel?: string;
  /**
   * **placeholder / 色塊態**的框高。預設維持 5:2 比例(既有呼叫端行為);D3 燈箱
   * 的封面條是固定 104px,所以那條路徑改傳高度。⚠️ 圖片態不吃這個值 —— 高度一律
   * 跟原圖(見 {@link PREVIEW_IMAGE_HEIGHT_RANGE})。
   */
  height?: CSSProperties['height'];
}

/**
 * 圖片態預覽的高度 clamp(R366)。上傳後的預覽必須「按原圖呈現」(owner
 * 2026-09-22):比例正確、不裁切 —— 固定高度條 + cover 會把直式圖裁到只剩中段,
 * 而那個畫面讀起來像「傳錯圖了」。min 對齊 D3 燈箱封面條的 104(placeholder ↔
 * 圖片態不跳版);max 擋直式圖把表單推到看不見(560px 燈箱裡 9:16 圖原比例會有
 * 九百多px 高)—— 貼到界時 `CoverSurface` 以 contain 完整顯示、留襯底不裁切。
 */
const PREVIEW_IMAGE_HEIGHT_RANGE = { min: 104, max: 260 };

export function CoverPreview({ picker, previewLabel = '', height }: CoverPreviewProps) {
  const { t } = useTranslation('common');
  const { previewCover, disabled, openFileDialog } = picker;
  const isImage = previewCover.kind === 'image';
  return (
    <CoverSurface
      data-testid="cover-picker-preview"
      cover={previewCover}
      fallback="linear-gradient(135deg, var(--ink-800), var(--ink-700))"
      imageFit="natural"
      naturalHeightRange={PREVIEW_IMAGE_HEIGHT_RANGE}
      style={{
        borderRadius: 'var(--r-sm)',
        // 兩個燈箱的 body 都是 overflow:auto 的 flex column:內容超高時 flex 會
        // 「先把固定高度的子項壓扁、再開始捲動」—— 預覽被壓成一條細線(R366
        // 事故一,建立新專案燈箱在 727px 高的視窗實拍)。捲動歸捲動,預覽不縮。
        flexShrink: 0,
        // 圖片態的高度交給圖片本身(natural clamp);placeholder / 色塊維持版面條
        ...(isImage
          ? {}
          : height === undefined
            ? { aspectRatio: '5 / 2' }
            : { height }),
      }}
    >
      <button
        type="button"
        onClick={openFileDialog}
        disabled={disabled}
        title={t('cover.picker.previewCta')}
        data-testid="cover-picker-preview-button"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          border: 'none',
          // 壓一層 scrim 而不是給文字加 textShadow:使用者上傳的照片可能很亮,
          // shadow 擋不住、scrim 擋得住。`--overlay-ink` 就是 theme.css 為
          // 「壓在媒體上的遮罩基底」定義的 token(深淺色主題各有值)。
          background:
            'linear-gradient(to top, color-mix(in oklab, var(--overlay-ink) 62%, transparent), color-mix(in oklab, var(--overlay-ink) 18%, transparent))',
          cursor: disabled ? 'default' : 'pointer',
          color: 'var(--on-accent)',
          padding: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {previewLabel.trim() || t('cover.picker.untitled')}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            opacity: 0.9,
          }}
        >
          <HaiIcon name="up" size={13} />
          {t('cover.picker.previewCta')}
        </span>
      </button>
    </CoverSurface>
  );
}

export function CoverControls({ picker }: { picker: CoverPickerController }) {
  const { t } = useTranslation('common');
  const { value, disabled, error, openFileDialog, selectColor, backToColor, inputRef, onFileChange } =
    picker;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* ── 色票列 + 上傳鈕 ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {COVER_SWATCHES.map((sw) => {
          const active = value.kind === 'color' && value.color === sw.hex;
          const label = t(`cover.swatch.${sw.labelKey}` as const);
          return (
            <button
              key={sw.id}
              type="button"
              disabled={disabled}
              onClick={() => selectColor(sw.hex)}
              aria-label={t('cover.picker.swatchAria', { name: label })}
              aria-pressed={active}
              title={label}
              data-testid={`cover-swatch-${sw.id}`}
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: sw.hex,
                border: active ? '2px solid var(--tide-600)' : '1px solid var(--hairline-strong)',
                // 未選取時**不寫** `outline: 'none'`:inline 會蓋掉全站 `:focus-visible` 焦點環
                // (specificity 0),Tab 到色票上看不出焦點在哪(CIS 交互)
                outline: active ? '1px solid var(--tide-200)' : undefined,
                cursor: disabled ? 'default' : 'pointer',
                padding: 0,
              }}
            />
          );
        })}

        <button
          type="button"
          onClick={openFileDialog}
          disabled={disabled}
          data-testid="cover-upload-button"
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '5px 10px',
            fontSize: 11.5,
            fontWeight: 600,
            color: 'var(--ink-200)',
            background: 'var(--ink-850)',
            border: '1px solid var(--hairline-strong)',
            borderRadius: 'var(--r-sm)',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <HaiIcon name="up" size={13} />
          {t('cover.picker.uploadButton')}
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_PICKER_ACCEPT}
          onChange={onFileChange}
          style={{ display: 'none' }}
          data-testid="cover-file-input"
        />
      </div>

      {/* ── hint / 本地錯誤 ─────────────────────────────────────────────── */}
      {/* CIS:錯誤 = 玫瑰 rose-700(`--danger-500` 在白底只有 3.76:1);hint = 次文字
          (`--ink-600` 以下不可當文字色) */}
      {error ? (
        <p role="alert" style={{ margin: 0, fontSize: 11, color: 'var(--rose-700)' }}>
          {error}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-500)' }}>
          {value.kind === 'image' ? (
            <>
              {t('cover.picker.imageSelected')}
              {'　'}
              <button
                type="button"
                onClick={backToColor}
                data-testid="cover-clear-image"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 11,
                  // teal 文字一律 `--concept-fg`(tide-600 當字只有 3.74:1)
                  color: 'var(--concept-fg)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {t('cover.picker.backToColor')}
              </button>
            </>
          ) : (
            t('cover.picker.hint')
          )}
        </p>
      )}
    </div>
  );
}

interface CoverPickerProps {
  value: CoverDraft;
  onChange: (next: CoverDraft) => void;
  /** 疊在預覽上的文字(專案名稱);空字串顯示 placeholder。 */
  previewLabel?: string;
  disabled?: boolean;
}

export function CoverPicker({
  value,
  onChange,
  previewLabel = '',
  disabled = false,
}: CoverPickerProps) {
  const picker = useCoverPicker({ value, onChange, disabled });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <CoverPreview picker={picker} previewLabel={previewLabel} />
      <CoverControls picker={picker} />
    </div>
  );
}
